import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { useCachedSearchTopOffers } from 'components/Accommodation/Offers/useCachedSearchTopOffers';
import { useIsRoomOfferAppLocked } from 'hooks/useIsRoomOfferAppLocked';
import { getOfferContextFromRoomContext } from 'middleware/analytics/contexts/roomContext';
import { getRoomsActiveFilters } from 'modules/accommodation/selectors';
import { getMeta } from 'modules/meta/selectors';
import { getAnchorPriceNightlyPerRoom, getIsAnchorHotel, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getAnchorPriceTotal } from 'modules/sapiSearch/utils';
import { getIsLockedDealExcludingGHA } from 'modules/search/selectors';
import { getHotelApiRoomsParameters } from 'modules/searchApi/selectors';
import { RootState } from 'store';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import { persistAppLockedDealImpressionsCount } from '@daedalus/core/src/_web/appLockedDeal/impressionsCount';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity, Page, RoomContext, Team } from '@daedalus/core/src/analytics/types/Events';
import { defaultCurrencyCode } from '@daedalus/core/src/localization/business/currency';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getCancellationDeadline, getRefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { calculateChargeDate, hasChargeLaterTag } from '@daedalus/core/src/offer/business/chargeLater';
import { getIsPayAtPropertyMoR } from '@daedalus/core/src/offer/business/getIsPayAtPropertyMoR';
import { getRoomOfferBoardType } from '@daedalus/core/src/offer/business/meal';
import { hasOfferTag } from '@daedalus/core/src/offer/business/offers';
import { Offer, OfferTagLabel, RateChecker } from '@daedalus/core/src/offer/types/offer';
import { getDisplayPrice, getPriceDetails, getTotalPrice, getTotalUpFrontPrice } from '@daedalus/core/src/price/business/price';
import { roomsFilterListToMap } from '@daedalus/core/src/room/business/utils/filters';
import { Room } from '@daedalus/core/src/room/types/room';
import { useGetHotelQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { useVioTag } from '@daedalus/core/src/tag';
import { OfferAlertBanner } from '@daedalus/shared/src/booking/OfferAlertBanner';
import { OfferAlertVariant } from '@daedalus/shared/src/booking/OfferAlertBanner/constants';
import { OfferDetails } from '@daedalus/shared/src/booking/OfferDetails';
import { useShouldShowDealFreezeButton } from '@daedalus/shared/src/dealFreeze/hooks/useShouldShowDealFreezeButton';
import { getFrozenBookingByHotelId } from '@daedalus/shared/src/dealFreeze/modules/selector';
import { RoomDetailsMountComponent } from '../RoomCard/constants';
import { RoomOfferCTA } from '../RoomOfferCTA';
import { RoomOfferPrice } from '../RoomOfferPrice/RoomOfferPrice';
import { RoomOfferVioTag } from '../RoomOfferVioTag';
import { DealFreezeBanner } from './DealFreezeBanner';
import { useHandlePriceDetailsClick } from './useHandlePriceDetailsClick';
interface Props {
  hotelId: string;
  room: Room;
  offer: Offer;
  component: RoomDetailsMountComponent;
  roomContext: RoomContext;
  offerIndex: number;
  isOfferSoldOut?: boolean;
}
const ResponsiveFlexWrapper = /*#__PURE__*/styled('div')({
  name: "ResponsiveFlexWrapper",
  class: "r19joj8a",
  propsAsIs: false
});
const DetailsConstraintBSide = /*#__PURE__*/styled('div')({
  name: "DetailsConstraintBSide",
  class: "d133x0x6",
  propsAsIs: false
});
export const RoomOffer = ({
  hotelId,
  room,
  offer,
  component,
  roomContext,
  offerIndex,
  isOfferSoldOut = false
}: Props) => {
  const {
    id,
    cancellationPenalties,
    prices,
    isClicked,
    matchType,
    matchedOfferPriceDiff
  } = offer;
  const dispatch = useDispatch();
  const isLockedDeal = useSelector(getIsLockedDealExcludingGHA(offer)) && !offer.isClicked;
  const {
    currencyCode
  } = useSelector(getMeta);
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 1
  });
  const {
    data: hotel
  } = useGetHotelQuery({
    hotelId
  }, {
    skip: !hotelId
  });
  const cancellationDeadline = getCancellationDeadline(cancellationPenalties);
  const displayPrice = getDisplayPrice(prices);
  const totalChargeablePrice = getTotalUpFrontPrice(displayPrice);
  const offerTotalPrice = getTotalPrice(displayPrice);
  const refundType = getRefundType(cancellationPenalties);
  const {
    isMobile
  } = useDeviceLayout();
  const {
    checkIn,
    checkOut
  } = useSelector(state => getHotelApiRoomsParameters(state, hotelId));
  const {
    isRoomOfferAppLocked,
    appDownloadLinkId
  } = useIsRoomOfferAppLocked({
    offerId: id
  });
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId));
  const isAnchorAppLockedDeal = isAnchorHotel && isRoomOfferAppLocked;
  const impressionDayCount = persistAppLockedDealImpressionsCount(isAnchorAppLockedDeal);
  const shouldShowDealFreezeButton = useShouldShowDealFreezeButton({
    offer,
    checkIn,
    checkOut,
    currentDate: new Date().toString(),
    isOfferAppLocked: isRoomOfferAppLocked,
    hotelId,
    isOfferSoldOut
  });
  const frozenHotel = useSelector((state: RootState) => getFrozenBookingByHotelId(state, hotelId));
  const shouldShowDealFreezeBanner = shouldShowDealFreezeButton && !frozenHotel;
  const {
    nightlyDisplayTotal
  } = displayPrice.chargeable;
  const matchedOfferPriceDiffPercent = matchedOfferPriceDiff / offerTotalPrice * 100;
  const offerBoardType = getRoomOfferBoardType(offer.services);
  const activeRoomsFilters = useSelector(getRoomsActiveFilters);
  const offerContext = roomContext && offer && getOfferContextFromRoomContext(roomContext, offer.id);
  const analyticsContext = useMemo(() => {
    if (!(roomContext && offerContext)) return null;
    return {
      [AnalyticsContext.RoomContext]: roomContext,
      [AnalyticsContext.OfferContext]: offerContext
    };
  }, [offerContext, roomContext]);
  const handlePriceDetailsClick = useHandlePriceDetailsClick({
    hotelId,
    room,
    offer,
    component,
    analyticsContext
  });
  useEffect(() => {
    if (inView) {
      dispatch(trackEvent({
        category: Category.User,
        entity: Entity.RoomOffer,
        action: Action.Viewed,
        team: Team.Select,
        page: Page.Accommodation,
        component,
        payload: {
          isSelectedOffer: Boolean(isClicked),
          matchType,
          matchedOfferPriceDiffPercent,
          roomFilterApplied: activeRoomsFilters.length > 0,
          allSelectedRoomFilters: roomsFilterListToMap(activeRoomsFilters)
        },
        analyticsContext
      }));
      if (isRoomOfferAppLocked) dispatch(trackEvent({
        category: Category.System,
        entity: Entity.AppExclusiveOffer,
        action: Action.Displayed,
        page: Page.Accommodation,
        team: Team.Select,
        payload: {
          bannerId: appDownloadLinkId,
          impressionDayCount
        },
        analyticsContext
      }));
      if (isOfferSoldOut) {
        dispatch(trackEvent({
          category: Category.System,
          entity: Entity.RoomOfferSoldOut,
          action: Action.Displayed,
          page: Page.Accommodation,
          team: Team.Select,
          component,
          analyticsContext
        }));
      }
    }
  }, [dispatch, inView]);
  const trackCancellationButtonClick = () => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.CancellationPolicyDetailButton,
      action: Action.Clicked,
      team: Team.Select,
      page: Page.Accommodation,
      component,
      analyticsContext
    }));
    dispatch(trackEvent({
      category: Category.System,
      entity: Entity.CancellationPolicyDetail,
      action: Action.Displayed,
      team: Team.Select,
      page: Page.Accommodation,
      component,
      analyticsContext
    }));
  };
  const chargeDate = calculateChargeDate(offer.cancellationPenalties);
  const hasChargeLater = hasChargeLaterTag(offer);
  const canPayLater = getIsPayAtPropertyMoR(offer);
  const {
    totalAtPropertyDisplayPrice,
    totalUpFrontDisplayPrice
  } = getPriceDetails(prices, canPayLater);
  const trackChargeLaterLabelClick = () => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.ChargeLaterDetailsButton,
      action: Action.Clicked,
      team: Team.Select,
      page: Page.Accommodation,
      component,
      analyticsContext
    }));
  };
  const isColumnCTALayout = !shouldShowDealFreezeButton || !isMobile;
  const {
    offerEntity
  } = useCachedSearchTopOffers({
    hotelId
  });
  const anchorPrice = useSelector((state: RootState) => getAnchorPriceNightlyPerRoom(state, offerEntity));
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const shownOfferPrice = showTotalPrices ? offerTotalPrice : nightlyDisplayTotal;
  const shownAnchorPrice = showTotalPrices ? anchorPriceTotal : anchorPrice;
  const {
    brandCode,
    brandName
  } = useBrand();
  const vioTagDetails = useVioTag({
    price: shownOfferPrice,
    anchorPrice: shownAnchorPrice,
    currencyCode,
    brandCode
  });
  const isCheapestOffer = roomContext?.roomPosition === 1 && offerIndex === 0;
  const isVioTagVisible = isCheapestOffer && vioTagDetails.isVioTagVisible;
  const isYourChoice = hasOfferTag(offer, OfferTagLabel.IsYourChoice);
  return <>
      {isClicked === true && matchType === 'by_terms' && !isOfferSoldOut && <Box paddingBottom="s400">
          <OfferAlertBanner matchedOfferPriceDiff={matchedOfferPriceDiff} currency={currencyCode || defaultCurrencyCode} variant={matchedOfferPriceDiff > 0 ? OfferAlertVariant.PriceIncrease : OfferAlertVariant.PriceDrop} />
        </Box>}
      <ResponsiveFlexWrapper ref={ref} className={cx(isMobile && '__isMobile')} data-id={isYourChoice && RateChecker.GHAYourChoiceId}>
        <DetailsConstraintBSide>
          {isOfferSoldOut && <Box display="flex" justifyContent="flex-start" alignItems="center" gap="s250">
              <Icon name="Forbid" size="sm" colorPath="content.danger.c600" />

              <Text variant="labelM" colorPath="content.danger.c600">
                <FormattedMessageWrapper id="roomOffer.soldOutMessage" defaultMessage="Sold out on {brandName}" values={{
              brandName
            }} />
              </Text>
            </Box>}

          <Box display="flex" justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} gap={isMobile ? 's250' : 's600'}>
            <div>
              {isVioTagVisible && !isOfferSoldOut && <RoomOfferVioTag vioTagDetails={vioTagDetails} />}
              <OfferDetails isOfferSoldOut={isOfferSoldOut} refundType={refundType} payLater={offer?.canPayLater} boardType={offerBoardType} freeCancellationDeadline={cancellationDeadline} cancellationPenalties={cancellationPenalties} totalPrice={totalChargeablePrice} totalUpFrontDisplayPrice={totalUpFrontDisplayPrice} currencyCode={currencyCode || defaultCurrencyCode} onCancellationButtonClick={trackCancellationButtonClick} isChargeLaterEligible={hasChargeLater} chargeLaterDate={chargeDate} onChargeLaterLabelClicked={trackChargeLaterLabelClick} hotelTimeZone={hotel?.timezone} payAtPropertyPrice={totalAtPropertyDisplayPrice} hideFreeCancellationDeadlineDate prefersReducedMotion />
            </div>
            <ResponsiveFlexWrapper className={cx(isMobile && '__isMobile')}>
              <Box display="flex" flexDirection="column" alignItems="flex-end">
                {isLockedDeal && !isOfferSoldOut && <Text colorPath="content.special.c500" variant="titleS" align="right">
                    <FormattedMessage id="signInToUnlockLabel" defaultMessage="Sign in to unlock" />
                  </Text>}
                <RoomOfferPrice hotelId={hotelId} offer={offer} onClickPriceDetails={handlePriceDetailsClick} shouldShowDealFreezeButton={shouldShowDealFreezeButton} isVioTagVisible={isVioTagVisible} isOfferSoldOut={isOfferSoldOut} />
              </Box>
              {isColumnCTALayout && <RoomOfferCTA hotelId={hotelId} room={room} offer={offer} component={component} roomContext={roomContext} offerContext={offerContext} shouldShowDealFreezeButton={shouldShowDealFreezeButton} isOfferSoldOut={isOfferSoldOut} />}
            </ResponsiveFlexWrapper>
          </Box>
          {shouldShowDealFreezeBanner && <DealFreezeBanner roomId={room.id} />}
          {!isColumnCTALayout && <Box paddingTop="s400">
              <RoomOfferCTA hotelId={hotelId} room={room} offer={offer} component={component} roomContext={roomContext} offerContext={offerContext} shouldShowDealFreezeButton={shouldShowDealFreezeButton} isOfferSoldOut={isOfferSoldOut} />
            </Box>}
        </DetailsConstraintBSide>
      </ResponsiveFlexWrapper>
    </>;
};

require("./RoomOffer.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomOffer.tsx");